import { isEmpty, mapValues, sortBy, uniq } from "lodash";
import { createSelector } from "reselect";

import { IntegrationType } from "constants/integrations";

import { withRegistration } from "lib/businessRegistrations";
import { getCombinedLotNumber } from "lib/saleLot";
import { getDateFromISO8601DateString } from "lib/timeFormats";

import { getIsSaleyardAdmin } from "../auth";
import { getActiveBusinesses } from "../businesses";
import { getCurrentSale } from "../currentSale";
import { getActiveLivestockAgentDeployment } from "../deploymentActiveAgent";
import {
  selectBuyerIdBySaleLotIdLookup,
  selectorInvoiceToBusinessIdBySaleLotIdLookup,
  selectorThirdPartyBusinessIdBySaleLotIdLookup,
  selectVendorIdBySaleLotIdLookup,
} from "../indexes";
import { selectIntegrationBusinessesByBusinessIdByTypeLookup } from "../integrationBusinesses";
import { selectResolvedInterestSettingsByBusinessIdLookup } from "../interest";
import { selectorCategorisedBusinessIdsFromCurrentSaleManualAdjustments } from "../manualAdjustments";
import {
  getAuctionPens,
  getBidders,
  getBusinesses,
  getBusinessRegistrations,
  getBusinessRelations,
  getCarrierCharges,
  getConsignments,
  getEContractAgreements,
  getEContracts,
  getNominations,
  getProperties,
  getSaleBuyerSplits,
  getSaleLots,
  getSaleVendorSplits,
} from "../root";
import { selectVendorCommissionBandsByBusinessIdLookup } from "../vendorCommissions";

export const BusinessTypes = {
  BUYER: "Buyer",
  BUYER_SPLIT: "Buyer Split",
  INVOICEE: "Invoicee",
  VENDOR: "Vendor",
  VENDOR_SPLIT: "Vendor Split",
  TRANSPORTER: "Transporter",
  THIRD_PARTY: "Third Party",
  SUNDRY_TO: "Sundry To",
  SUNDRY_FROM: "Sundry From",
  CARRIER: "Carrier",
  REGISTERED_BIDDER: "Registered Bidder",
  PEN_OWNER: "Pen Owner",
  NOMINATION_VENDOR: "Nomination Vendor",
  NOMINATION_TRANSPORTER: "Nomination Transporter",
  DEFAULT_VENDOR: "Default Vendor",
  DEFAULT_BUYER: "Default Buyer",
  ECONTRACT_VENDOR: "eContract Vendor",
  ECONTRACT_BUYER: "eContract Buyer",
};

const ids = values => uniq(values.filter(Boolean));

export const selectBusinessesIdsInSaleByType = createSelector(
  [
    selectVendorIdBySaleLotIdLookup,
    selectBuyerIdBySaleLotIdLookup,
    selectorInvoiceToBusinessIdBySaleLotIdLookup,
    selectorThirdPartyBusinessIdBySaleLotIdLookup,
    selectorCategorisedBusinessIdsFromCurrentSaleManualAdjustments,
    getSaleVendorSplits,
    getSaleBuyerSplits,
    getSaleLots,
    getConsignments,
    getCarrierCharges,
    getBidders,
    getAuctionPens,
    getNominations,
    getCurrentSale,
    getEContracts,
    getEContractAgreements,
  ],
  (
    vendorIdBySaleLotIdLookup,
    buyerIdBySaleLotIdLookup,
    invoiceToBusinessIdBySaleLotIdLookup,
    thirdPartyBusinessIdBySaleLotIdLookup,
    categorisedBusinessIdsFromCurrentSaleManualAdjustments,
    saleVendorSplits,
    saleBuyerSplits,
    saleLots,
    consignments,
    carrierCharges,
    bidders,
    auctionPens,
    nominations,
    currentSale,
    eContracts,
    eContractAgreements,
  ) => {
    // eContracts are loaded for every Sale - only count this Sale's.
    const deploymentSaleIds = new Set(
      (currentSale?.deployment_sales || []).map(
        deploymentSale => deploymentSale.deployment_sale_id,
      ),
    );
    const saleEContracts = [
      ...Object.values(eContracts),
      ...Object.values(eContractAgreements),
    ].filter(eContract => deploymentSaleIds.has(eContract.deploymentSaleId));

    return {
      [BusinessTypes.BUYER]: uniq(Object.values(buyerIdBySaleLotIdLookup)),
      [BusinessTypes.BUYER_SPLIT]: uniq(
        Object.values(saleBuyerSplits)
          .flatMap(bs => bs.splits.map(s => s.businessId))
          .filter(Boolean),
      ),
      [BusinessTypes.INVOICEE]: uniq(
        Object.values(invoiceToBusinessIdBySaleLotIdLookup),
      ),
      [BusinessTypes.THIRD_PARTY]: uniq(
        Object.values(thirdPartyBusinessIdBySaleLotIdLookup),
      ),
      // Includes vendors of consignments with no lots yet.
      [BusinessTypes.VENDOR]: ids([
        ...Object.values(vendorIdBySaleLotIdLookup),
        ...Object.values(consignments).map(
          consignment => consignment.vendor_id,
        ),
      ]),
      [BusinessTypes.VENDOR_SPLIT]: uniq(
        Object.values(saleVendorSplits)
          .flatMap(vs => vs.splits.map(s => s.businessId))
          .filter(Boolean),
      ),
      [BusinessTypes.SUNDRY_FROM]: uniq(
        categorisedBusinessIdsFromCurrentSaleManualAdjustments.from.filter(
          Boolean,
        ),
      ),
      [BusinessTypes.SUNDRY_TO]: uniq(
        categorisedBusinessIdsFromCurrentSaleManualAdjustments.to.filter(
          Boolean,
        ),
      ),
      [BusinessTypes.TRANSPORTER]: ids(
        Object.values(saleLots).map(saleLot => saleLot.transporterId),
      ),
      [BusinessTypes.CARRIER]: ids(
        Object.values(consignments).map(
          consignment => carrierCharges[consignment.carrierChargeId]?.carrier,
        ),
      ),
      [BusinessTypes.REGISTERED_BIDDER]: ids(
        Object.values(bidders).map(bidder => bidder.businessId),
      ),
      [BusinessTypes.PEN_OWNER]: ids(
        Object.values(auctionPens).flatMap(auctionPen =>
          (auctionPen.penOwners || []).map(penOwner => penOwner.businessId),
        ),
      ),
      [BusinessTypes.NOMINATION_VENDOR]: ids(
        Object.values(nominations).map(nomination => nomination.vendorId),
      ),
      [BusinessTypes.NOMINATION_TRANSPORTER]: ids(
        Object.values(nominations).map(nomination => nomination.transporterId),
      ),
      [BusinessTypes.DEFAULT_VENDOR]: ids([currentSale?.default_vendor_id]),
      [BusinessTypes.DEFAULT_BUYER]: ids([currentSale?.default_buyer_id]),
      [BusinessTypes.ECONTRACT_VENDOR]: ids(
        saleEContracts.map(eContract => eContract.vendorBusinessId),
      ),
      [BusinessTypes.ECONTRACT_BUYER]: ids(
        saleEContracts.map(eContract => eContract.buyerBusinessId),
      ),
    };
  },
);

export const selectBusinessesInSaleByIdLookup = createSelector(
  [
    getBusinesses,
    selectBusinessesIdsInSaleByType,
    selectResolvedInterestSettingsByBusinessIdLookup,
    selectVendorCommissionBandsByBusinessIdLookup,
    getActiveLivestockAgentDeployment,
    selectIntegrationBusinessesByBusinessIdByTypeLookup,
    getBusinessRegistrations,
    getSaleLots,
    getConsignments,
    selectVendorIdBySaleLotIdLookup,
    selectBuyerIdBySaleLotIdLookup,
    selectorInvoiceToBusinessIdBySaleLotIdLookup,
    selectorThirdPartyBusinessIdBySaleLotIdLookup,
  ],
  (
    businesses,
    businessIdsByType,
    resolvedInterestSettingsByBusinessId,
    vendorCommissionsByBusinessId,
    activeLivestockAgentDeployment,
    integrationBusinessesByTypeByBusinessIdLookup,
    registrationsById,
    saleLots,
    consignments,
    vendorIdBySaleLotId,
    buyerIdBySaleLotId,
    invoiceToIdBySaleLotId,
    thirdPartyIdBySaleLotId,
  ) => {
    const result = {};

    Object.entries(businessIdsByType).forEach(([businessType, businessIds]) => {
      businessIds.forEach(businessId => {
        const business = businesses[businessId] || {};

        if (isEmpty(business)) {
          return;
        }

        const xeroIntegrationBusiness =
          integrationBusinessesByTypeByBusinessIdLookup[IntegrationType.Xero]?.[
            businessId
          ]?.[0] || null;

        const lastModified = business.lastModified
          ? getDateFromISO8601DateString(business.lastModified)
          : null;

        if (businessId in result) {
          result[businessId].roleInSale.push(businessType);
        } else {
          result[businessId] = {
            business: withRegistration(business, registrationsById),
            roleInSale: [businessType],
            vendorNumbers: [],
            lotNumbers: [],
            lotCount: 0,
            headCount: 0,
            lastModified,
            xeroIntegrationBusiness,
            deployment: activeLivestockAgentDeployment,
            interestSettings: resolvedInterestSettingsByBusinessId[businessId],
            vendorCommissionBands:
              vendorCommissionsByBusinessId[businessId] || [],
          };
        }
      });
    });

    Object.values(consignments).forEach(consignment => {
      const vendorNumbers = result[consignment.vendor_id]?.vendorNumbers;
      const vendorNumber = consignment.vendorNumber
        ? String(consignment.vendorNumber)
        : null;
      if (
        vendorNumbers &&
        vendorNumber &&
        !vendorNumbers.includes(vendorNumber)
      ) {
        vendorNumbers.push(vendorNumber);
      }
    });

    const lotRoleLookups = [
      vendorIdBySaleLotId,
      buyerIdBySaleLotId,
      invoiceToIdBySaleLotId,
      thirdPartyIdBySaleLotId,
    ];
    sortBy(Object.values(saleLots), [
      saleLot => Number(saleLot.lot_number) || Number.MAX_SAFE_INTEGER,
      saleLot => saleLot.lotNumberSuffix || "",
    ]).forEach(saleLot => {
      // A Business can hold several roles on one lot - count the lot once.
      new Set(lotRoleLookups.map(lookup => lookup[saleLot.id])).forEach(
        businessId => {
          const businessInSale = result[businessId];
          if (!businessInSale) {
            return;
          }
          businessInSale.lotCount += 1;
          businessInSale.headCount += saleLot.quantity || 0;
          if (saleLot.lot_number) {
            businessInSale.lotNumbers.push(getCombinedLotNumber(saleLot));
          }
        },
      );
    });

    return result;
  },
);

export const getBusinessesInSaleById = businessId => state =>
  selectBusinessesInSaleByIdLookup(state)[businessId];

export const selectBusinessesInSaleAggridData = createSelector(
  [selectBusinessesInSaleByIdLookup],
  businessesInSaleLookup => Object.values(businessesInSaleLookup),
);

export const selectOptionsForBusinessField = createSelector(
  [
    getBusinesses,
    selectVendorIdBySaleLotIdLookup,
    selectBuyerIdBySaleLotIdLookup,
    selectorInvoiceToBusinessIdBySaleLotIdLookup,
    selectorThirdPartyBusinessIdBySaleLotIdLookup,
    selectorCategorisedBusinessIdsFromCurrentSaleManualAdjustments,
    getActiveBusinesses,
    getIsSaleyardAdmin,
    getProperties,
    getBusinessRelations,
    getBusinessRegistrations,
  ],
  (
    businessByIdLookup,
    vendorIdBySaleLotIdLookup,
    buyerIdBySaleLotIdLookup,
    invoiceToBusinessIdBySaleLotIdLookup,
    thirdPartyBusinessIdBySaleLotIdLookup,
    categorisedBusinessIdsFromCurrentSaleManualAdjustments,
    activeBusinessByIdLookup,
    isSaleyardAdmin,
    getPropertyLookup,
    businessRelationsLookup,
    registrationsById,
  ) => {
    // Copies, so the flags below aren't written onto the store's businesses.
    const businesses = mapValues(businessByIdLookup, business =>
      withRegistration(business, registrationsById),
    );

    for (const vendorId of Object.values(vendorIdBySaleLotIdLookup)) {
      if (businesses[vendorId]) {
        businesses[vendorId].isVendor = true;
        businesses[vendorId].inSale = true;
      }
    }

    for (const buyerId of Object.values(buyerIdBySaleLotIdLookup)) {
      if (businesses[buyerId]) {
        businesses[buyerId].isBuyer = true;
        businesses[buyerId].inSale = true;
      }
    }

    for (const invoiceToId of Object.values(
      invoiceToBusinessIdBySaleLotIdLookup,
    )) {
      if (businesses[invoiceToId]) {
        businesses[invoiceToId].invoiceTo = true;
        businesses[invoiceToId].isBuyer = true;
        businesses[invoiceToId].inSale = true;
      }
    }

    for (const thirdPartyId of Object.values(
      thirdPartyBusinessIdBySaleLotIdLookup,
    )) {
      if (businesses[thirdPartyId]) {
        businesses[thirdPartyId].thirdParty = true;
        businesses[thirdPartyId].isBuyer = true;
        businesses[thirdPartyId].inSale = true;
      }
    }

    for (const activeBusinessId of Object.values(activeBusinessByIdLookup)) {
      if (businesses[activeBusinessId]) {
        businesses[activeBusinessId].isActive = true;
      }
    }

    for (const businessRelation of Object.values(businessRelationsLookup)) {
      if (businesses[businessRelation.relatedFromId]) {
        businesses[businessRelation.relatedFromId].isRelatedFrom = true;
      }
      if (businesses[businessRelation.relatedToId]) {
        businesses[businessRelation.relatedToId].isRelatedTo = true;
      }
    }

    for (const manualAdjustmentBusinessIds of Object.values(
      categorisedBusinessIdsFromCurrentSaleManualAdjustments,
    )) {
      // Broken up into from/to, but we will just classify as being involved in sundry.
      for (const manualAdjustmentBusinessId of manualAdjustmentBusinessIds) {
        if (businesses[manualAdjustmentBusinessId]) {
          businesses[manualAdjustmentBusinessId].isSundry = true;
        }
      }
    }

    for (const business of Object.values(businesses)) {
      const inActive = business.isActive ? "" : " (INACTIVE)";
      const code = isSaleyardAdmin
        ? business.shortCodeSaleyard
        : business.shortCode;

      business.properties =
        business?.properties?.map(p => ({
          PIC: getPropertyLookup[p.id]?.PIC || "",
          ...p,
        })) || [];
      business.label = `${code ? `(${code})` : ""} ${business.name}${inActive}`;
    }

    return businesses;
  },
);
